import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Dialog, DialogTitle } from '@/components/ui/Dialog'
import { cn } from '@/lib/utils'

export interface CardImage {
  url: string
  label: string
}

interface CardImageModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cardName: string
  images: CardImage[]
  startIndex?: number
}

export default function CardImageModal({
  open,
  onOpenChange,
  cardName,
  images,
  startIndex = 0,
}: CardImageModalProps) {
  const [index, setIndex] = useState(startIndex)

  // Reset to the clicked thumbnail each time the modal opens.
  useEffect(() => {
    if (open) setIndex(startIndex)
  }, [open, startIndex])

  if (images.length === 0) return null

  const safeIndex = Math.min(index, images.length - 1)
  const current = images[safeIndex]
  const multiple = images.length > 1

  const go = (delta: number) =>
    setIndex((i) => (i + delta + images.length) % images.length)

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="w-[672px]">
      <DialogTitle className="text-base">
        {cardName}
        <span className="ml-2 text-sm text-fg-muted">{current.label}</span>
      </DialogTitle>

      <div className="relative flex items-center justify-center rounded-md bg-bg-subtle">
        <img
          src={current.url}
          alt={`${cardName} — ${current.label}`}
          className="max-h-[70vh] w-auto max-w-full rounded-md object-contain"
        />

        {multiple && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-bg/80 p-1.5 shadow-sm hover:bg-bg"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-bg/80 p-1.5 shadow-sm hover:bg-bg"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {multiple && (
        <div className="flex justify-center gap-2">
          {images.map((img, i) => (
            <button
              key={img.label}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show ${img.label}`}
              className={cn(
                'h-12 w-12 overflow-hidden rounded-md border-2 bg-bg-subtle transition-colors',
                i === safeIndex ? 'border-fg' : 'border-transparent hover:border-border'
              )}
            >
              <img src={img.url} alt={img.label} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </Dialog>
  )
}
